import { API_ROUTES } from '@/constants/apiRoutes';
import {
  BrandingThemeAddSubmission,
  BrandingThemeItem,
} from '@/types/brandingThemes';
import { IdempotentRequest } from '@/types/common';
import { api } from '@/utils/api';

export async function getBrandingThemesListService(queryParams: string) {
  const { data } = await api<BrandingThemeItem[]>({
    url: `${API_ROUTES.BRANDING_THEMES}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function getBrandingThemesDetailsService(uuid: string) {
  const { data } = await api<BrandingThemeItem>({
    url: `${API_ROUTES.BRANDING_THEMES}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
}

export async function updateDefaultBrandingThemeService(uuid: string) {
  const { data } = await api({
    url: `${API_ROUTES.BRANDING_THEMES}${uuid}/set-as-default/`,
    method: 'post',
    version: 2,
  });

  return data;
}

export async function deleteBrandingThemeService(uuid: string) {
  const { data } = await api({
    url: `${API_ROUTES.BRANDING_THEMES}${uuid}/`,
    method: 'delete',
    version: 2,
  });

  return data;
}

export async function copyBrandingThemeService(uuid: string) {
  const { data } = await api<{ uuid: string }>({
    url: `${API_ROUTES.BRANDING_THEMES}${uuid}/copy/`,
    method: 'post',
    version: 2,
  });

  return data.uuid;
}

function buildBrandingThemePayload(payload: BrandingThemeAddSubmission) {
  const {
    name,
    primaryColor,
    secondaryColor,
    buttonFontColor,
    logoUrl,
    isProgramDefault,
    mobileUi,
  } = payload;
  const {
    customBottomNavigationSelectedIconColor,
    customHeroBackground,
    customHomeScreenBackground,
    customNavigationBackground,
    titleColor,
    titleAccentSectionColor,
    titleHeroSectionColor,
    mobileLogoUrl,
    font,
    introVideo,
  } = mobileUi;

  return {
    name,
    is_program_default: isProgramDefault,
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    button_font_color: buttonFontColor,
    logo_url: logoUrl,
    mobile_ui: {
      style: 'Custom',
      custom_navigation_background: customNavigationBackground,
      custom_hero_background: customHeroBackground,
      custom_home_screen_background: customHomeScreenBackground,
      primary_color: payload.mobileUi.primaryColor,
      secondary_color: payload.mobileUi.secondaryColor,
      custom_bottom_navigation_selected_icon_color:
        customBottomNavigationSelectedIconColor,
      title_color: titleColor,
      title_hero_section_color: titleHeroSectionColor,
      title_accent_section_color: titleAccentSectionColor,
      mobile_logo_url: mobileLogoUrl,
      intro_video: introVideo,
      splash_screen: '1 Advisor',
      font: font,
    },
  };
}

export async function addBrandingThemeService(
  payload: IdempotentRequest<BrandingThemeAddSubmission>,
) {
  const dataPayload = buildBrandingThemePayload(payload);

  const { data } = await api({
    url: `${API_ROUTES.BRANDING_THEMES}`,
    method: 'post',
    version: 2,
    data: dataPayload,
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
}

export async function editBrandingThemeService(
  uuid: string,
  payload: IdempotentRequest<BrandingThemeAddSubmission>,
) {
  const dataPayload = buildBrandingThemePayload(payload);
  const { data } = await api({
    url: `${API_ROUTES.BRANDING_THEMES}${uuid}/`,
    method: 'put',
    version: 2,
    data: { ...dataPayload, uuid },
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
}

export async function getBrandingThemePresignedUrlService(
  file: string,
  file_extension: string,
) {
  const { data } = await api<string>({
    url: `${API_ROUTES.BRANDING_THEMES}generate-pre-signed-upload-url/`,
    method: 'post',
    version: 2,
    data: {
      file,
      file_extension,
    },
  });

  return data;
}
